<script setup lang="ts">
import type { DialogStepperRailVariant } from "./types";
import { watch } from "vue";
import { useProvideDialogStepperState } from "./useDialogStepperState";

interface Props {
  totalSteps: number
  /**
   * Names of the steps, in order. The rail shows them beside the dots and uses them as the dots' accessible
   * labels. Without one name per step the rail shows bare numbered dots.
   */
  stepLabels?: string[]
  /**
   * Makes the completed steps of the rail clickable, so the user can jump back without pressing Previous
   * repeatedly. Forward stays blocked: a step is only reached through its guard. Turn it off while the
   * wizard is busy.
   */
  railInteractive?: boolean
  /** Look of the rail in the footer; `current` is the App Store wizard rail. */
  railVariant?: DialogStepperRailVariant
}

const props = withDefaults(defineProps<Props>(), {
  stepLabels: undefined,
  railInteractive: false,
  railVariant: "auto",
});

const emit = defineEmits<{
  (e: "onChange", currentStep: number): void
}>();

const modelValue = defineModel<number>("modelValue", { default: 0 });

useProvideDialogStepperState({
  modelValue,
  totalSteps: () => props.totalSteps,
  stepLabels: () => props.stepLabels,
  railInteractive: () => props.railInteractive,
  railVariant: () => props.railVariant,
});

// Watch for changes to emit the onChange event
watch(modelValue, (newValue) => {
  emit("onChange", newValue);
});
</script>

<template>
  <div class="dialog-stepper flex flex-col flex-1 min-h-0">
    <slot />
  </div>
</template>
